// HEAL-44b §5.1 — лист правки одной распознанной строки.
//
// Человек загрузил бланк, модель прочитала «Фолиевая кислота (B9) — 5,2 нг/мл»,
// а в бланке 4,1. Или прочитала B9 там, где B12. Здесь он это исправляет — до
// подтверждения, пока анализ ещё черновик.

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Loader2, Search, Trash2 } from 'lucide-react';
import { api } from '@/api/client';
import { capitalizeFirst } from '@/lib/utils';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { EnrichedBiomarker, ReviewEditItem, ReviewWarning } from '@/api/types';

type CatalogItem = {
  code: string;
  name_ru: string;
  category_label_ru: string;
  accepted_units: string[];
};

type Props = {
  marker: EnrichedBiomarker | null;
  warning: ReviewWarning | null;
  pending: boolean;
  onClose: () => void;
  onSave: (edit: ReviewEditItem) => void;
  onRemove: (sourceIndex: number) => void;
};

export function EditMarkerSheet({ marker, warning, pending, onClose, onSave, onRemove }: Props) {
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState<string | null>(null);
  const [reassign, setReassign] = useState<CatalogItem | null>(null);
  const [picking, setPicking] = useState(false);

  // При открытии на новом показателе начинаем с того, что видит человек.
  useEffect(() => {
    if (marker === null) return;
    setValue(displayValue(marker));
    setUnit(marker.unit);
    setReassign(null);
    setPicking(false);
  }, [marker]);

  if (marker === null) return null;

  const sourceIndex = marker.source_index;
  const canEdit = sourceIndex !== undefined;

  // Единицы: после переназначения — допустимые для НОВОЙ карточки, иначе —
  // допустимые для текущей (их отдаёт обогатитель). Раньше здесь лежала только
  // текущая единица, и поправить её, не переназначая показатель, было
  // невозможно — а это как раз частый случай: карточка распознана верно, а OCR
  // прочитал «мг/дл» вместо «ммоль/л».
  const units =
    reassign?.accepted_units ??
    (marker.accepted_units?.length ? marker.accepted_units : marker.unit ? [marker.unit] : []);

  // Новая карточка требует единицу, а прежняя ей не подошла — человек должен
  // выбрать сам. Отправлять как есть значило бы получить отказ сервера на
  // ровном месте.
  const unitMissing = reassign !== null && reassign.accepted_units.length > 0 && unit === null;

  const handleDone = () => {
    if (!canEdit) return;

    const edit: ReviewEditItem = { source_index: sourceIndex };
    const original = displayValue(marker);

    const valueChanged = value.trim() !== original;
    const unitChanged = unit !== marker.unit;

    if (valueChanged) edit.value = value.trim();
    if (reassign !== null) edit.biomarker_code = reassign.code;

    // Единицу шлём всегда, когда она менялась или менялось значение: сервер
    // трактует число именно в присланной единице. Если не прислать ничего, он
    // подставит ту, что показывал экран, — поведение то же, просто на его стороне.
    if (unitChanged || valueChanged) edit.unit = unit;

    // Переназначение на безразмерную карточку (индексы, соотношения): единицу
    // надо явно снять, иначе старый «%» останется итоговой и сервер откажет.
    // Снятие разрешено только для карточки, у которой единиц действительно нет.
    if (reassign !== null && reassign.accepted_units.length === 0) {
      edit.unit = null;
    }

    // Ничего не изменилось — не тревожим сервер и не плодим событий аналитики.
    if (edit.value === undefined && edit.biomarker_code === undefined && !unitChanged) {
      onClose();
      return;
    }

    onSave(edit);
  };

  return (
    <BottomSheet open onClose={onClose} ariaLabel="Правка показателя">
      {picking ? (
        <MarkerPicker
          onBack={() => setPicking(false)}
          onPick={(item) => {
            setReassign(item);
            // Если новая карточка принимает ту же единицу, что человек видел, —
            // оставляем её: число на экране измерено именно в ней. Если не
            // принимает, обнуляем и ждём явного выбора, а не подставляем первую
            // попавшуюся: иначе показанное число молча оказалось бы в чужой шкале.
            setUnit(item.accepted_units.includes(unit ?? '') ? unit : null);
            setPicking(false);
          }}
        />
      ) : (
        <div className="flex flex-col gap-4 p-6 pb-[calc(1.5rem_+_env(safe-area-inset-bottom))]">
          <div>
            <h2 className="text-lg font-semibold leading-snug">
              {capitalizeFirst(reassign?.name_ru ?? marker.name_ru)}
            </h2>
            {/* Имя с бланка — то, с чем человек сверяется. Модель нередко
                переписывает его по-своему («Фолиевая к-та» → «Фолиевая кислота
                (B9)»), и показывать здесь каноническое имя значит показать одно
                и то же дважды. Если raw_name совпал с каноническим — строку
                прячем, она ничего не добавляет. */}
            {marker.raw_name && marker.raw_name !== marker.name_ru && (
              <p className="mt-1 text-sm text-muted-foreground">
                На бланке: «{capitalizeFirst(marker.raw_name)}»
              </p>
            )}
          </div>

          <label className="block">
            <span className="mb-1.5 block text-hl-small font-semibold text-gray-700">Значение</span>
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              inputMode="decimal"
              aria-label="Значение показателя"
            />
          </label>

          {warning !== null && (
            <p className="rounded-control bg-amber-50 px-3 py-2 text-hl-small text-amber-800">
              {warning.message}
            </p>
          )}

          {units.length > 0 && (
            <label className="block">
              <span className="mb-1.5 block text-hl-small font-semibold text-gray-700">Единица</span>
              {/* Выбор, а не свободный ввод: вписать ммоль/л в карточку,
                  размеченную в мг/дл, значит молча испортить историю. */}
              {/* Всегда управляемый: пустая строка = «не выбрано». С undefined
                  компонент на первом кадре был неуправляемым и переключался
                  после эффекта — React справедливо ругался. */}
              <Select value={unit ?? ''} onValueChange={setUnit}>
                <SelectTrigger aria-label="Единица измерения">
                  <SelectValue placeholder="Выберите единицу" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          )}

          <button
            type="button"
            onClick={() => setPicking(true)}
            className="flex w-full items-center justify-between rounded-md border border-border px-3 py-3 text-left text-sm"
          >
            <span>Это другой показатель</span>
            <Search className="h-4 w-4 flex-shrink-0 text-gray-400" />
          </button>

          {/* Действия стопкой, как в остальных листах приложения: главное во всю
              ширину, второстепенное текстом под ним. Рядом в строке они читались
              как равные, хотя «Убрать строку» — разрушающее и редкое. */}
          <div className="mt-1 flex flex-col gap-2">
            <Button
              type="button"
              onClick={handleDone}
              disabled={pending || !canEdit || unitMissing}
              className="w-full"
              size="lg"
            >
              {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Готово'}
            </Button>
            <button
              type="button"
              onClick={() => canEdit && onRemove(sourceIndex)}
              disabled={pending || !canEdit}
              className="flex w-full items-center justify-center gap-2 py-2 text-sm font-medium text-rose-600 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              Убрать строку
            </button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}

function MarkerPicker({
  onBack,
  onPick,
}: {
  onBack: () => void;
  onPick: (item: CatalogItem) => void;
}) {
  const [query, setQuery] = useState('');
  const trimmed = query.trim();

  const { data, isFetching } = useQuery({
    queryKey: ['biomarkers', 'catalog', { q: trimmed }],
    queryFn: async () =>
      (await api.get<{ items: CatalogItem[] }>('/biomarkers/catalog', { params: { q: trimmed } }))
        .data,
    // Каталог не меняется между запросами человека — держим подольше.
    staleTime: 5 * 60 * 1000,
  });

  const items = useMemo(() => data?.items ?? [], [data]);

  return (
    <div className="flex max-h-[70vh] flex-col gap-3 p-6 pb-[calc(1.5rem_+_env(safe-area-inset-bottom))]">
      <div className="flex items-center gap-2">
        <button type="button" onClick={onBack} aria-label="Назад" className="p-1">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h2 className="text-lg font-semibold">Выберите показатель</h2>
      </div>

      <Input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Название показателя"
        aria-label="Поиск показателя"
      />

      <div className="flex-1 overflow-y-auto">
        {isFetching && items.length === 0 ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-hl-secondary text-muted-foreground">
            Ничего не нашли. Попробуйте другое написание.
          </p>
        ) : (
          <div className="flex flex-col">
            {items.map((item) => (
              <button
                key={item.code}
                type="button"
                onClick={() => onPick(item)}
                className="flex flex-col items-start border-b border-border py-2.5 text-left last:border-b-0"
              >
                <span className="text-hl-secondary font-medium">{capitalizeFirst(item.name_ru)}</span>
                <span className="text-hl-small text-gray-400">{item.category_label_ru}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** То же написание, что показывает карточка: запятая, слово вместо числа. */
function displayValue(marker: EnrichedBiomarker): string {
  if (marker.value_qualitative) return marker.value_qualitative;
  if (marker.value === null) return '';
  return String(marker.value).replace('.', ',');
}
